/**
 * Popover, SlideOver, Modal, Toast, Timeline, PinPad — the rest of plan §3.6.
 *
 * Shadows are the only elevation in the app (plan §3.3): cards are flat, and
 * only these floating surfaces carry one.
 */
import {
  useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.js'
import { placeFloating } from '../lib/place-floating.js'
import { Button } from './primitives.js'
import { IconClose } from '../frame/icons.js'

/** Closes on Escape and on a click outside. Used by every header dropdown. */
export function useDismiss(
  open: boolean, onClose: () => void, ref: React.RefObject<HTMLElement>, layer = 0,
) {
  useEffect(() => {
    if (!open) return
    watchKeydown()

    const onKey = (event: KeyboardEvent) => {
      /*
       * Escape belongs to whatever is on top. Without this, one press closed
       * the step-up *and* the dialog that raised it, abandoning the action the
       * person was half-way through confirming.
       */
      if (event.key === 'Escape' && layer >= layerAtKeydown()) onClose()
    }
    const onPointer = (event: MouseEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) return
      /*
       * A click inside something drawn *above* this is not an outside click.
       *
       * Every overlay's scrim carries its layer, so the one the click landed
       * in can be compared with this one. Without it, typing a password into
       * the step-up dialog dismissed the dialog that raised it on the very
       * first mousedown — `contains` is false for a sibling portal, whatever
       * it is stacked over.
       */
      const landedIn = (event.target as Element | null)?.closest?.('[data-overlay-layer]')
      if (landedIn && Number(landedIn.getAttribute('data-overlay-layer')) > layer) return
      onClose()
    }

    document.addEventListener('keydown', onKey)
    // `true` so the outside click is seen before a handler inside stops it.
    document.addEventListener('mousedown', onPointer, true)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer, true)
    }
  }, [open, onClose, ref, layer])
}

/*
 * The top layer *as the key went down*, not as it is when this listener runs.
 *
 * React flushes a discrete event's updates before the document's own
 * listeners see it, so a popover that closes itself on Escape — the picker
 * over the service pop-out — is already gone from the DOM by then, and the
 * box under it read itself as the top and closed too. One capture listener
 * takes the reading first; the comparisons stay in the bubble phase, so a
 * field that stops Escape from propagating still keeps it.
 */
let keydownTop = 0
let listening = false
function layerAtKeydown(): number {
  return keydownTop
}
function watchKeydown(): void {
  if (listening || typeof document === 'undefined') return
  listening = true
  document.addEventListener('keydown', () => { keydownTop = topLayer() }, true)
}

/**
 * Does this Escape belong to the overlay at `layer`? The same reading
 * `useDismiss` makes, for a surface that listens on its own panel rather than
 * on the document — `Sheet`, whose children include popovers that are not
 * portalled, so their Escape bubbles up through the sheet's own node.
 */
export function escapeIsFor(layer: number): boolean {
  return layer >= layerAtKeydown()
}

/** Start taking the top-layer reading at keydown; idempotent. */
export function watchOverlayKeys(): void {
  watchKeydown()
}

/** The highest layer currently drawn, read from the scrims themselves. */
function topLayer(): number {
  let top = 0
  for (const node of document.querySelectorAll('[data-overlay-layer]')) {
    top = Math.max(top, Number(node.getAttribute('data-overlay-layer')) || 0)
  }
  return top
}

export interface PopoverProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  /** Which edge to hang from; the header's right-hand group opens leftwards. */
  align?: 'left' | 'right'
  width?: number
  className?: string
  label: string
  /**
   * The button this hangs off, when it has to escape a clipping ancestor.
   *
   * A related list is a card with `overflow: hidden` so its corners stay
   * round, and an absolutely-placed panel inside one is a panel cut off at the
   * card's bottom edge. Given an anchor, this measures that button and places
   * itself `fixed` instead — the same trick `MenuSelect` and the calendar use,
   * and for the same reason.
   */
  anchor?: HTMLElement | null
  /**
   * A ceiling of its own, under whatever the viewport allows.
   *
   * A picker whose list fills a 1400px screen is a picker nobody can read the
   * bottom of without losing the search box at the top. Editors leave this
   * alone: a form is as tall as its fields.
   */
  maxHeight?: number
  /**
   * A `data-*` name the panel carries, so a test can find every popover of
   * one kind — `editor-shell` is how `tests/ui/as-drawn.spec.ts` measures
   * that every builder popover is 480 wide.
   */
  marker?: string
  /**
   * The overlay layer, when this opens over a modal — a recipe line's popover
   * over the catalog's service pop-out. Marked on the panel so the box under
   * it leaves Escape and its clicks alone; left out, a popover is part of
   * whatever page it hangs off, as before.
   */
  layer?: number
  /** Placed on whole pixels (`placeFloating`'s `snap`) — the visit page's popovers; off for everything else. */
  snap?: boolean
}


export function Popover({
  open, onClose, children, align = 'left', width = 320, label, className, anchor,
  maxHeight, marker, layer, snap = false,
}: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(open, onClose, ref, layer ?? 0)

  const floating = anchor !== undefined
  const [at, setAt] = useState<{ left: number; top: number; maxHeight: number } | null>(null)

  /*
   * How tall the content wants to be, last time anybody asked.
   *
   * Kept in a ref rather than re-derived, because finding it out costs a DOM
   * write (below) and the answer only changes when the content does.
   */
  const wanted = useRef(0)

  /*
   * Put the panel where `wanted` says it goes. **Reads only.**
   *
   * That this touches nothing is the whole point of it being separate from
   * `measure`. It is what runs on every scroll and every resize, and the
   * version of this code that did the measuring there too is the one Aaron
   * hit: "i can't scroll through the options it's presenting. the scrollable
   * section just flickers when i try to scroll."
   *
   * Scrolling the list inside the panel fires the capture-phase scroll
   * listener; the measure took the cap off; with the cap off the list no
   * longer overflowed, so the browser clamped its `scrollTop` to 0; the cap
   * went back on and the list was at the top again. Every wheel tick, forty
   * times a second. The panel flickered and the list never moved.
   */
  const apply = useCallback(() => {
    const rect = anchor?.getBoundingClientRect()
    if (!ref.current || !rect) return

    /*
     * `placeFloating` — the one rule, shared with every menu and lookup.
     *
     * This used to centre itself and take the whole window when neither side
     * was comfortable, which meant the *same* picker was a dropdown when the
     * row was high on the page and a full-screen dialog when it was low. A
     * control that changes into something else depending on where it is is a
     * control nobody can learn. It is always a panel now; when it cannot have
     * the height it wants it scrolls inside itself.
     */
    const found = placeFloating({
      anchor: rect,
      width,
      wanted: Math.min(wanted.current, maxHeight ?? Number.POSITIVE_INFINITY),
      align,
      snap,
    })

    /* Only when it actually moved, so this cannot re-render in a loop. */
    setAt((current) => (current
      && current.left === found.left
      && current.top === found.top
      && current.maxHeight === found.maxHeight
      ? current
      : { left: found.left, top: found.top, maxHeight: found.maxHeight }))
  }, [anchor, align, width, maxHeight, snap])

  /*
   * Find out how tall the content wants to be, then place it. **Writes.**
   *
   * The measurement has to happen with the cap off, or it answers the wrong
   * question: `getBoundingClientRect().height` on a capped panel is "how tall
   * were you last allowed", not "how tall do you want to be", and feeding
   * that back in is a cap that can only ratchet down — 460 to 240 to 180 and
   * stuck there, which is the picker in Aaron's first screenshot.
   *
   * Taking the cap off resets the `scrollTop` of anything inside that was
   * scrolled, so those are put back. Both happen inside one layout effect,
   * before paint, so neither is ever seen.
   */
  const measure = useCallback(() => {
    const node = ref.current
    if (!node) return

    const scrolled = Array.from(node.querySelectorAll('*'))
      .filter((child): child is HTMLElement =>
        child instanceof HTMLElement && child.scrollTop > 0)
      .map((child) => [child, child.scrollTop] as const)

    const held = node.style.maxHeight
    node.style.maxHeight = 'none'
    wanted.current = node.getBoundingClientRect().height
    node.style.maxHeight = held

    for (const [child, top] of scrolled) child.scrollTop = top

    apply()
  }, [apply])

  /*
   * `children` is the dependency that matters, and a `ResizeObserver` on the
   * panel is not a substitute for it — that was an earlier attempt and it
   * does not work, for a reason worth writing down: once the panel is capped,
   * content arriving inside it does *not* change the panel's own box, so the
   * observer never fires and the stale cap sustains itself. A re-render is
   * the honest signal that what is inside has changed.
   */
  useLayoutEffect(() => {
    if (!open || !floating) { setAt(null); return }
    measure()

    /*
     * Scrolling *inside* the panel does not move the anchor, so there is
     * nothing to re-place — and the panel's own list is the thing most often
     * scrolled. Everything else (a page, a card, a scrolling table the row
     * sits in) genuinely moves the anchor and has to be followed.
     */
    const onScroll = (event: Event) => {
      const target = event.target
      if (target instanceof Node && ref.current?.contains(target)) return
      apply()
    }

    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', apply)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', apply)
    }
  }, [open, floating, measure, apply, children])

  if (!open) return null

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      {...(marker ? { [`data-${marker}`]: '' } : {})}
      {...(layer !== undefined ? { 'data-overlay-layer': layer } : {})}
      style={floating
        ? { width, left: at?.left ?? 0, top: at?.top ?? 0, maxHeight: at?.maxHeight }
        : { width }}
      className={cn(
        // A column, so a header and footer inside it can hold their ground
        // while the middle scrolls.
        /*
         * 14, not 10 — `design-system.md` §3: a popup (an editor, a picker, a
         * control panel) is `rounded-popup`, and `rounded-card` is the 10px
         * radius of the list pages built before folder 16. A caller that is a
         * *menu* passes `rounded-menu` (16) over this.
         */
        'z-50 flex flex-col overflow-hidden rounded-popup border border-line bg-surface',
        'shadow-popover',
        /*
         * Never wider than the screen it is on, whatever it asked for.
         *
         * 24 is `placeFloating`'s MARGIN on both sides, so the cap and the
         * placement agree: once the wanted width is more than the window can
         * hold, that function's clamp puts `left` at MARGIN and this puts the
         * right-hand edge at MARGIN too. The Filter panel is 360px and a
         * small phone is 360px wide, so this is not hypothetical.
         */
        'max-w-[calc(100vw-24px)]',
        floating
          /*
           * Measured before it is placed, so it must not flash at 0,0 —
           * transparent rather than `invisible`, because `visibility: hidden`
           * makes everything inside unfocusable, and `autoFocus` runs in that
           * very first commit. Hidden that way, every popover with a field in
           * it opened with the caret still on the page behind it.
           */
          ? cn('fixed', at ? 'visible' : 'opacity-0 pointer-events-none')
          : cn('absolute top-[calc(100%+6px)]', align === 'right' ? 'right-0' : 'left-0'),
        className,
      )}
    >
      {children}
    </div>
  )
}

export interface SlideOverProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  width?: number
}

/** Right-hand panel, 360–440px (plan §3.6). */
export function SlideOver({ open, onClose, title, children, footer, width = 400 }: SlideOverProps) {
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(open, onClose, ref)
  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/20">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        /*
         * A width, not a *fixed* width: a 560px panel on a 390px phone puts
         * its left edge off the screen, and since the page itself does not
         * scroll sideways there is no way to reach what fell off. The panel
         * takes the screen when the screen is the smaller of the two.
         */
        style={{ width, maxWidth: '100%' }}
        className="flex h-full flex-col bg-surface shadow-slideover"
      >
        <header className="flex h-[52px] shrink-0 items-center justify-between border-b border-line px-4">
          <h2 className="text-card">{title}</h2>
          <Button variant="ghost" aria-label="Close" onClick={onClose}>×</Button>
        </header>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        {footer && <footer className="shrink-0 border-t border-line p-4">{footer}</footer>}
      </div>
    </div>
  )
}

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  /**
   * The quiet clause beside the title — "Jobs · 32 fields".
   *
   * Beside rather than under: a dialog header is one line, and what this
   * says is about the title rather than about the body.
   */
  subtitle?: string
  /**
   * The tile left of the title, when the dialog is about one particular thing
   * — the mail tile on **Connect the sending mailbox**. `Sheet` takes the same
   * prop for the same reason: a dialog that names a vendor should show it.
   */
  mark?: ReactNode
  /** A control in the header, left of the close — a Builder/Text segment. */
  headerRight?: ReactNode
  children: ReactNode
  footer?: ReactNode
  /**
   * The left of the footer — one clause about what pressing the primary will
   * do ("Recorded in the audit log."). `Sheet` has always had one; a dialog
   * that asks for a decision needs it more, because the sentence that makes
   * the decision safe has nowhere else to go.
   */
  footerNote?: ReactNode
  /**
   * How wide, in pixels. 520 suits a form; the card builder is wider because
   * it draws its live preview beside the four picks rather than under them.
   */
  width?: number
  /*
   * Which layer it sits on. 50 is the ordinary one — a dialog over the page.
   * A dialog raised by a form that is *itself* stacked over another form has
   * to clear both, so `Sheet` passes its own layer down; see the note there.
   * `STEP_UP_LAYER` is the top of the scale, for the one dialog that can be
   * raised from inside anything.
   */
  layer?: number
}

/**
 * Above every other layer in the product — 50 ordinary, 70 stacked, 80 the
 * floating panels (`Sheet`'s note has the scale).
 *
 * The step-up dialog is the one thing that can be raised from *inside* any of
 * them: a key flyout at 70 asks for a code, and a dialog at 50 drew behind it.
 * It was not merely hard to read — clicking a code box landed on the flyout's
 * scrim, which dismissed the dialog and abandoned the save, so the keys could
 * not be saved at all.
 */
export const STEP_UP_LAYER = 90

export function Modal({
  open, onClose, title, subtitle, mark, headerRight, children, footer, footerNote,
  width = 520, layer = 50,
}: ModalProps) {
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(open, onClose, ref, layer)
  if (!open) return null

  /*
   * Portalled to `document.body`, like `Sheet`, so where it is *written* can
   * never decide where it is *drawn*.
   *
   * `Sheet` portals and this did not, so a dialog rendered beside a sheet
   * landed earlier in the document than the sheet's portal node — and at the
   * same z-index the later node wins. `Sheet` already carries a note about
   * this from the inside; the same thing bit from the outside, on the one
   * dialog where it mattered most.
   */
  return createPortal((
    <div
      style={{ zIndex: layer }}
      data-overlay-layer={layer}
      className="fixed inset-0 flex items-center justify-center bg-black/25 p-4"
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ maxWidth: width }}
        // §3: a modal dialog is 16.
        className="flex max-h-sheet w-full flex-col overflow-hidden rounded-dialog border border-line bg-surface shadow-popover"
      >
        {/*
          * The × is not decoration. Clicking the backdrop closes a dialog and
          * always has, but nothing on screen says so — and a person who does
          * not already know that has no visible way out of a dialog they
          * opened by accident.
          */}
        <header className="flex items-center gap-2.5 border-b border-line-faint px-4 py-3">
          {mark}
          <h2 className="text-card">{title}</h2>
          {subtitle && <span className="truncate text-sub text-text-muted">{subtitle}</span>}
          <span className="ml-auto flex shrink-0 items-center gap-2">
            {headerRight}
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="-mr-1 shrink-0 rounded-field p-1 text-text-muted hover:bg-line-soft hover:text-text"
            >
              <IconClose size={16} />
            </button>
          </span>
        </header>
        {/* The body scrolls, not the page behind it: a tall dialog that pushed
            its own footer off the screen hid the button that saves it. */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4 text-body">{children}</div>
        {(footer || footerNote) && (
          /* The same footer `Sheet` draws, so a dialog and a flyout asking for
             the same decision do not look like two different products. */
          <footer className={cn('flex shrink-0 items-center gap-2 border-t border-line',
            'bg-surface-alt px-4 py-3')}>
            <span className="min-w-0 text-[12px] leading-[1.4] text-text-faint">{footerNote}</span>
            <span className="ml-auto flex shrink-0 items-center gap-2">{footer}</span>
          </footer>
        )}
      </div>
    </div>
  ), document.body)
}

export interface ConfirmDialogProps {
  /** What is about to happen, named: "Remove Option B?" */
  title: string
  body: ReactNode
  confirmLabel?: string
  /** What the button says while it is working. Defaults to "Removing…". */
  busyLabel?: string
  tone?: 'default' | 'danger'
  busy?: boolean
  /**
   * Not yet answerable — a confirm that asks for a reason and has not been
   * given one. Separate from `busy` because the two look different: busy says
   * "working on it", disabled says "there is still something to fill in", and
   * a dialog that says "Cancelling…" before you have typed anything is lying.
   */
  disabled?: boolean
  /** What the quiet button says. "Cancel" unless the third answer needs it. */
  cancelLabel?: string
  /**
   * A third answer, at the footer's left.
   *
   * Two buttons can only ask a yes/no question, and "you have unsaved
   * changes" is not one: discard, keep editing and save are three different
   * intentions, and collapsing them makes somebody who meant to save press
   * Cancel and lose the work anyway. The odd one out goes left, away from the
   * pair, because a red button beside a green one is a coin toss at speed.
   */
  extra?: ReactNode
  /** Passed straight to `Modal` — see the note on `ModalProps.layer`. */
  layer?: number
  onConfirm: () => void
  onCancel: () => void
}

/**
 * The one confirm in the product.
 *
 * Every destructive action asks the same way, in the same place, with the
 * thing it is about to remove named in the title — "Remove Option B?", not
 * "Are you sure?". A dialog that does not say what it is about makes people
 * click Cancel and go looking, or click Confirm and find out.
 */
export function ConfirmDialog({
  title, body, confirmLabel = 'Remove', busyLabel = 'Removing…', tone = 'danger',
  busy, disabled, cancelLabel = 'Cancel', extra, layer, onConfirm, onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      open
      layer={layer}
      onClose={onCancel}
      title={title}
      footer={
        <>
          {extra}
          <Button onClick={onCancel} disabled={busy}>{cancelLabel}</Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            disabled={busy || disabled}
            onClick={onConfirm}
          >
            {busy ? busyLabel : confirmLabel}
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  )
}

/**
 * The × on a tab that stands for something a person made — a quote option, a
 * saved view. Sits inside the tab's button rather than beside it, so the hit
 * areas do not fight, and it is a `span` because a button inside a button is
 * invalid markup that browsers resolve unpredictably.
 */
export function TabRemove({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={`Remove ${label}`}
      title={`Remove ${label}`}
      onClick={(event) => { event.stopPropagation(); onRemove() }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          event.stopPropagation()
          onRemove()
        }
      }}
      className="ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-chip text-text-faint hover:bg-danger-bg hover:text-danger"
    >
      ×
    </span>
  )
}

export interface ToastProps {
  tone?: 'default' | 'success' | 'warn' | 'danger'
  children: ReactNode
  onDismiss?: () => void
  /**
   * One thing the toast can offer — **Undo**, or **See why**.
   *
   * A bulk action that changed a hundred and twenty-six records and then said
   * so in a sentence has told somebody what happened and given them nothing
   * to do about it. The two things worth doing are putting it back and
   * finding out what was skipped, and both belong here rather than in a
   * dialog that interrupts, because neither is urgent (v3 §3.4).
   */
  action?: { label: string; onClick: () => void }
}

const TOAST_TONES = {
  default: 'border-line bg-surface text-text',
  success: 'border-accent-line bg-accent-soft text-accent-dark',
  warn: 'border-warn/30 bg-warn-bg text-warn',
  danger: 'border-danger-ring bg-danger-bg text-danger',
}

export function Toast({ tone = 'default', children, onDismiss, action }: ToastProps) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-center gap-3 rounded-card border px-3 py-2.5 text-cell shadow-popover',
        TOAST_TONES[tone],
      )}
    >
      <span className="flex-1">{children}</span>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="shrink-0 font-semibold underline underline-offset-2"
        >
          {action.label}
        </button>
      )}
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="text-text-faint">
          ×
        </button>
      )}
    </div>
  )
}

export interface TimelineEntry {
  id: string
  at: string
  who: string
  what: ReactNode
  /** Rendered muted under the line — the old → new of a field change. */
  detail?: ReactNode
}

/** The History tab on every record (plan §4.2). */
export function Timeline({ entries }: { entries: TimelineEntry[] }) {
  return (
    <ol className="m-0 list-none space-y-0 p-0">
      {entries.map((entry) => (
        <li key={entry.id} className="relative flex gap-3 py-2.5 pl-4">
          <span
            aria-hidden
            className="absolute left-0 top-[15px] h-[7px] w-[7px] rounded-full bg-line-strong"
          />
          <span
            aria-hidden
            className="absolute left-[3px] top-[22px] h-[calc(100%-14px)] w-px bg-line-soft last:hidden"
          />
          <div className="flex-1">
            <p className="m-0 text-cell text-text">{entry.what}</p>
            {entry.detail && <p className="m-0 text-sub text-text-muted">{entry.detail}</p>}
          </div>
          <span className="shrink-0 text-sub text-text-faint">
            {entry.who} · {entry.at}
          </span>
        </li>
      ))}
    </ol>
  )
}

export interface PinPadProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  maxLength?: number
}

/**
 * Crew clock-in keypad — 64px keys, white on the `line` border (plan §3.7).
 * A PIN is 4 to 6 digits (addendum §G.3).
 */
export function PinPad({ value, onChange, onSubmit, maxLength = 6 }: PinPadProps) {
  const press = (digit: string) => {
    if (value.length < maxLength) onChange(value + digit)
  }

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex gap-2" aria-label={`${value.length} of ${maxLength} digits entered`}>
        {Array.from({ length: maxLength }, (_, i) => (
          <span
            key={i}
            className={cn(
              'h-3 w-3 rounded-full border',
              i < value.length ? 'border-accent bg-accent' : 'border-line-strong bg-transparent',
            )}
          />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2.5">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <button
            key={digit}
            type="button"
            onClick={() => press(digit)}
            className="h-16 w-16 rounded-touch border border-line bg-surface text-[22px] font-semibold text-text active:bg-hover"
          >
            {digit}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange(value.slice(0, -1))}
          aria-label="Delete a digit"
          className="h-16 w-16 rounded-touch border border-line bg-surface text-[18px] text-text-muted active:bg-hover"
        >
          ⌫
        </button>
        <button
          type="button"
          onClick={() => press('0')}
          className="h-16 w-16 rounded-touch border border-line bg-surface text-[22px] font-semibold text-text active:bg-hover"
        >
          0
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={value.length < 4}
          aria-label="Sign in"
          className="h-16 w-16 rounded-touch border border-accent bg-accent text-[18px] font-semibold text-white disabled:opacity-40"
        >
          →
        </button>
      </div>
    </div>
  )
}
